<script lang="ts">
  /**
   * Horizontal alphabetical scroll bar.
   *
   * Adapted from https://github.com/mooalot/alphabetical-scroll-bar, laid out
   * left-to-right. It is NOT bound to a scrollable element — it just reports
   * which letter the pointer is over via `onletterchange`.
   *
   * Every letter is always shown, spread evenly across the width. The letter
   * under the pointer magnifies (a dock-style fisheye that pushes its
   * neighbours aside), and dragging/scrubbing reports each letter it crosses.
   * Letters not in `validLetters` are dimmed but otherwise behave normally.
   *
   * The zoom is deferred by ACTIVATE_DELAY so a quick tap still reports its
   * letter without kicking off the magnification; an actual drag zooms at once.
   */

  // Magnification tuning.
  const MULTIPLIER = 2; // scale of the letter directly under the pointer
  const CURVE = [1, 0.7, 0.5, 0.3, 0.1]; // falloff for neighbours, nearest first
  const ADVANCE_RATIO = 0.7; // approx glyph advance as a fraction of font-size
  const ACTIVATE_DELAY = 150; // ms the pointer must linger before the zoom begins
  const MOVE_THRESHOLD = 6; // px of travel that counts as a scrub (zoom at once)

  interface Props {
    /** Alphabet as a string or array of single characters. */
    alphabet?: string | string[];
    /** Letters present/selectable; others are dimmed. Defaults to all. */
    validLetters?: string[];
    /** Fired with the letter under the pointer as it changes. */
    onletterchange?: (letter: string) => void;
  }

  let {
    alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    validLetters,
    onletterchange,
  }: Props = $props();

  const letters = $derived(typeof alphabet === 'string' ? [...alphabet] : alphabet);
  const validSet = $derived(
    validLetters ? new Set(validLetters.map((l) => l.toLowerCase())) : null,
  );
  const isDimmed = (letter: string) =>
    validSet != null && !validSet.has(letter.toLowerCase());

  let container = $state<HTMLElement>();
  let fontSize = $state(16);

  let magIndex = $state<number | null>(null);
  let active = $state(false);

  let isDown = false;
  let lastEmittedLetter: string | undefined;
  let activateTimer: ReturnType<typeof setTimeout> | undefined;
  let startX = 0;

  // Track font-size (a viewport-based clamp) so the neighbour push stays sized
  // correctly across resizes.
  $effect(() => {
    if (!container) return;
    const measure = () => {
      fontSize = parseFloat(getComputedStyle(container!).fontSize) || fontSize;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  });

  // --- magnification ---------------------------------------------------------
  function letterScale(index: number): number {
    if (magIndex == null || !active) return 1;
    const rel = Math.abs(magIndex - index);
    return rel < CURVE.length - 1 ? CURVE[rel] * (MULTIPLIER - 1) + 1 : 1;
  }

  const scales = $derived(letters.map((_, i) => letterScale(i)));

  // Horizontal push so magnified letters don't overlap their neighbours: each
  // gap grows by the extra half-widths of the two scaled glyphs beside it, and
  // offsets accumulate outward from the focus (dock-style fisheye).
  const offsets = $derived.by(() => {
    const n = letters.length;
    const out = new Array<number>(n).fill(0);
    if (magIndex == null || !active) return out;

    const advance = fontSize * ADVANCE_RATIO;
    let acc = 0;
    for (let i = magIndex + 1; i < n; i++) {
      acc += (scales[i - 1] - 1 + (scales[i] - 1)) * (advance / 2);
      out[i] = acc;
    }
    acc = 0;
    for (let i = magIndex - 1; i >= 0; i--) {
      acc += (scales[i + 1] - 1 + (scales[i] - 1)) * (advance / 2);
      out[i] = -acc;
    }
    return out;
  });

  // --- pointer handling ------------------------------------------------------
  function letterAt(clientX: number): string | null {
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0) return null;
    const pos = Math.min(Math.max(0, clientX - rect.left), rect.width);
    magIndex = Math.round((pos / rect.width) * (letters.length - 1));
    return letters[magIndex] ?? null;
  }

  // Defer the zoom so a quick tap doesn't trigger it. Re-armed on each engage
  // point (press / mouse-enter); a drag past MOVE_THRESHOLD zooms immediately.
  function armActivation(x: number) {
    startX = x;
    clearTimeout(activateTimer);
    activateTimer = setTimeout(() => (active = true), ACTIVATE_DELAY);
  }

  function handle(e: PointerEvent, kind: 'down' | 'move' | 'enter') {
    const letter = letterAt(e.clientX);
    // A deliberate scrub should zoom right away rather than wait out the delay.
    if (!active && Math.abs(e.clientX - startX) > MOVE_THRESHOLD) {
      clearTimeout(activateTimer);
      active = true;
    }
    // Touch scrubbing reports continuously; a mouse only reports while pressed.
    const shouldEmit = e.pointerType !== 'mouse' || kind === 'down' || isDown;
    if (shouldEmit && letter != null && lastEmittedLetter !== letter) {
      lastEmittedLetter = letter;
      onletterchange?.(letter);
    }
  }

  function onpointerdown(e: PointerEvent) {
    isDown = true;
    container?.setPointerCapture(e.pointerId);
    armActivation(e.clientX);
    handle(e, 'down');
  }

  function onpointermove(e: PointerEvent) {
    handle(e, 'move');
  }

  function onpointerenter(e: PointerEvent) {
    if (e.pointerType !== 'mouse') return;
    armActivation(e.clientX);
    handle(e, 'enter');
  }

  function endInteraction(e?: PointerEvent) {
    isDown = false;
    active = false;
    magIndex = null;
    clearTimeout(activateTimer);
    if (e) container?.releasePointerCapture?.(e.pointerId);
  }
</script>

<div
  bind:this={container}
  class="asb"
  {onpointerdown}
  {onpointermove}
  {onpointerenter}
  onpointerup={endInteraction}
  onpointercancel={endInteraction}
  onpointerleave={() => !isDown && endInteraction()}
  role="slider"
  aria-label="Alphabetical scroll bar"
  aria-valuetext={magIndex != null ? letters[magIndex] : undefined}
  aria-valuemin={0}
  aria-valuemax={letters.length - 1}
  aria-valuenow={magIndex ?? 0}
  tabindex="0"
>
  {#each letters as letter, i (i)}
    <div
      class="letter"
      class:dimmed={isDimmed(letter)}
      style:transform={`translateX(${offsets[i]}px) scale(${scales[i]})`}
      style:z-index={magIndex === i ? 1 : 0}
    >
      <span>{letter}</span>
    </div>
  {/each}
</div>

<style>
  .asb {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    /* Not less than 12px, not more than 20px. */
    font-size: min(20px, max(12px, 2.5vw));
    /* Vertical breathing room so magnified letters have somewhere to grow. */
    padding: calc(1.5 * min(20px, max(12px, 2.5vw))) 0;
    cursor: pointer;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
  }

  .letter {
    position: relative;
    /* Widen the hit area vertically. */
    padding: 8px 0;
    pointer-events: none;
    transition: transform 0.15s ease-in-out;
    transform-origin: bottom center;
  }

  .letter span {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    line-height: 1;
  }

  .letter.dimmed {
    opacity: 0.3;
  }
</style>
